import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BdGrantRequestsService } from '@bindoc-projects/bindoc-permissions-lib/data';
import { ShareableType } from '@bindoc-projects/resource-domain-lib';
import { ButtonDirective } from 'primeng/button';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Message } from 'primeng/message';
import { Textarea } from 'primeng/textarea';
import { BdUiDialogPrimaryActionDirective } from '@bindoc-projects/ui-lib/dialog/ui';

export interface RequestAccessDialogData {
  shareableId: string;
  shareableType: ShareableType;
  shareableName?: string;
}

export interface RequestAccessDialogResult {
  requested: true;
}

@Component({
  selector: 'bd-request-access-dialog',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-3 p-4 min-w-[420px]">
      <p>
        Zugriff auf
        <strong>{{ data.shareableName ?? data.shareableId }}</strong>
        anfragen?
      </p>
      <label class="text-sm">Kommentar (optional)</label>
      <textarea
        pTextarea
        [rows]="3"
        [(ngModel)]="comment"
        [disabled]="duplicateDetected() || submitting()"
      ></textarea>
      @if (duplicateDetected()) {
        <p-message severity="warn"
          >Sie haben bereits eine Anfrage gestellt.</p-message
        >
      }
      @if (errorMessage()) {
        <p-message severity="error">{{ errorMessage() }}</p-message>
      }
      <div class="flex justify-end gap-2 pt-2">
        <button pButton text (click)="cancel()"><span>Abbrechen</span></button>
        <button
          pButton
          bdDialogPrimaryAction
          [disabled]="duplicateDetected() || submitting()"
          (click)="submit()"
        >
          <span>Anfragen</span>
        </button>
      </div>
    </div>
  `,
  imports: [
    BdUiDialogPrimaryActionDirective,
    FormsModule,
    ButtonDirective,
    Textarea,
    Message,
  ],
})
export class BdRequestAccessDialogComponent {
  private readonly _ref = inject(DynamicDialogRef);
  private readonly _config =
    inject<DynamicDialogConfig<RequestAccessDialogData>>(DynamicDialogConfig);
  private readonly _grantRequestsService = inject(BdGrantRequestsService);

  public readonly data: RequestAccessDialogData = this._requireData();

  public comment = '';
  public readonly submitting = signal(false);
  public readonly duplicateDetected = signal(false);
  public readonly errorMessage = signal('');

  public submit(): void {
    if (this.duplicateDetected() || this.submitting()) {
      return;
    }
    this.submitting.set(true);
    this.errorMessage.set('');
    this._grantRequestsService
      .create({
        shareableReference: {
          id: this.data.shareableId,
          type: this.data.shareableType,
        },
        comment: this.comment.trim() || undefined,
      })
      .subscribe({
        next: () => this._ref.close({ requested: true }),
        error: (err: HttpErrorResponse) => {
          this.submitting.set(false);
          if (err.status === 409) {
            this.duplicateDetected.set(true);
          } else {
            this.errorMessage.set('Anfrage konnte nicht gesendet werden.');
          }
        },
      });
  }

  public cancel(): void {
    this._ref.close(null);
  }

  private _requireData(): RequestAccessDialogData {
    if (!this._config.data) {
      throw new Error('BdRequestAccessDialogComponent requires dialog data');
    }
    return this._config.data;
  }
}
